import { ARTICLE_SETTINGS_PATH } from '../cms/ManageView';
import { VOICE_PATH } from '../reach/voiceWords';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { OrganizationSettings } from '../settings/Organization';
import { VoiceStyleSettings } from '../settings/VoiceStyle';
import { DefaultsSettings } from '../settings/Defaults';
import { BillingSettings } from '../settings/Billing';
import { SocialSettings } from '../settings/Social';
import { EventAuditSettings } from '../settings/EventAudit';
import { TeamSettings } from '../settings/Team';
import { IntegrationsSettings } from '../settings/Integrations';
import { ApprovalsSettings } from '../settings/AgentControls';
import { ResearchSettings } from '../settings/Research';
import { TaxonomySettings } from '../settings/Taxonomy';
import { TopicCustomFieldsSettings } from '../settings/TopicCustomFields';
import { PublicSiteSettings } from '../settings/PublicSite';
import { CollectionDataSettings } from '../settings/CollectionData';
import { CaptureDevicesSettings } from '../settings/CaptureDevices';
import { EbayConnectionSettings } from '../settings/EbayConnection';
import { PublishingSettings } from '../settings/Publishing';
import { DomainsSettings } from '../settings/Domains';
import { AnalyticsSettings } from '../settings/Analytics';
import { useProduct, ProductId } from '../../context/ProductContext';

type SettingSection =
  | 'organization'
  | 'voice'
  | 'agents'
  | 'approvals'
  | 'defaults'
  | 'billing'
  | 'social'
  | 'audit'
  | 'team'
  | 'integrations'
  | 'research'
  | 'taxonomy'
  | 'topicFields'
  | 'publicSite'
  | 'devices'
  | 'ebay'
  | 'collectionData'
  | 'publishing'
  | 'domains'
  | 'analytics';

interface NavItem {
  id: SettingSection;
  label: string;
  meta?: string;
  products?: ProductId[];
}

/**
 * The Account rail (UX optimization review, 2026-10-24): General, Voice & style, AI Agents,
 * Approvals, Team, Billing, API & sources, Activity log, with no counts. Account
 * opens on General. Profile is its own page (/profile, ProfilePage), and the
 * "All settings, A–Z" index is gone.
 */
const ACCOUNT_NAV: NavItem[] = [
  { id: 'organization', label: 'General' },
  { id: 'voice', label: 'Voice & style', products: ['atomizer', 'tinycms'] },
  { id: 'agents', label: 'AI Agents', products: ['atomizer', 'tinycms'] },
  { id: 'approvals', label: 'Approvals', products: ['atomizer', 'tinycms'] },
  { id: 'team', label: 'Team' },
  { id: 'billing', label: 'Billing' },
  { id: 'integrations', label: 'API & sources' },
  { id: 'audit', label: 'Activity Log' },
];

/**
 * Screens the review did not redraw. They stay reachable for the product that
 * owns them, after the design menu.
 */
const EXTRA_NAV: NavItem[] = [
  { id: 'defaults', label: 'Preferences', products: ['reach'] },
  { id: 'social', label: 'Connected accounts', products: ['reach'] },
  { id: 'taxonomy', label: 'Taxonomy', products: ['collect'] },
  { id: 'topicFields', label: 'Custom fields', products: ['collect'] },
  { id: 'publicSite', label: 'Public site', products: ['collect'] },
  { id: 'devices', label: 'Capture devices', products: ['collect'] },
  { id: 'ebay', label: 'eBay', products: ['collect'] },
  { id: 'collectionData', label: 'Your data', products: ['collect'] },
];

/** tinycms moved these out of Account. Old links land on the page that owns them now. */
/**
 * Every section is a path, like Profile always was: /settings/general,
 * /settings/team, /settings/connections, /settings/billing, /settings/activity.
 * The ids stay as they were; these are the words in the address. An old
 * ?section= link is sent to its path.
 */
const SLUG: Partial<Record<SettingSection, string>> = {
  organization: 'general',
  agents: 'ai-agents',
  integrations: 'connections',
  audit: 'activity',
};
export const settingsPath = (id: string) =>
  id === 'all' ? '/settings' : `/settings/${SLUG[id as SettingSection] || id}`;
const sectionOfSlug = (slug: string) =>
  (Object.entries(SLUG).find(([, s]) => s === slug)?.[0] as SettingSection | undefined) ||
  (slug as SettingSection);

const MOVED: Partial<Record<SettingSection, string>> = {
  taxonomy: '/prism-ai/settings#taxonomy',
  topicFields: `${ARTICLE_SETTINGS_PATH}#fields`,
  publishing: VOICE_PATH,
  publicSite: '/site#visibility',
  domains: '/site#domain',
  analytics: '/site#analytics',
};

export function SettingsLayout() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const legacy = params.get('section') as SettingSection | null;
  const pathSlug = /^\/settings\/([^/?#]+)/.exec(location.pathname)?.[1] || null;
  const requested: SettingSection | null =
    legacy || (pathSlug && pathSlug !== 'profile' ? sectionOfSlug(pathSlug) : null);
  const [activeSection, setActiveSection] = useState<SettingSection>(requested || 'organization');
  const { product } = useProduct();
  const isTiny = product === 'atomizer' || product === 'tinycms';

  const navItems = ACCOUNT_NAV.filter(
    (item) => !item.products || item.products.includes(product)
  ).concat(EXTRA_NAV.filter((item) => !item.products || item.products.includes(product)));
  const isVisible = (section: SettingSection) => navItems.some((item) => item.id === section);

  useEffect(() => {
    // Profile is its own page now (2026-10-24).
    if (pathSlug === 'profile') {
      navigate('/profile', { replace: true });
      return;
    }
    if (requested && isTiny && MOVED[requested]) {
      navigate(MOVED[requested] as string, { replace: true });
      return;
    }
    if (legacy) {
      navigate(settingsPath(legacy), { replace: true });
      return;
    }
    // Account opens on General; an unknown or hidden section does too.
    if (requested && isVisible(requested)) setActiveSection(requested);
    else setActiveSection('organization');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, requested, legacy, pathSlug]);

  const openSection = (id: SettingSection) => {
    setActiveSection(id);
    navigate(settingsPath(id));
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'voice':
        return <VoiceStyleSettings />;
      case 'agents':
        return <IntegrationsSettings part="agents" />;
      case 'approvals':
        return <ApprovalsSettings />;
      case 'defaults':
        return <DefaultsSettings />;
      case 'billing':
        return <BillingSettings />;
      case 'social':
        return <SocialSettings />;
      case 'audit':
        return <EventAuditSettings />;
      case 'team':
        return <TeamSettings />;
      case 'integrations':
        return <IntegrationsSettings />;
      case 'research':
        return <ResearchSettings />;
      case 'taxonomy':
        return <TaxonomySettings />;
      case 'topicFields':
        return <TopicCustomFieldsSettings />;
      case 'publicSite':
        return <PublicSiteSettings />;
      case 'devices':
        return <CaptureDevicesSettings />;
      case 'ebay':
        return <EbayConnectionSettings />;
      case 'collectionData':
        return <CollectionDataSettings />;
      case 'publishing':
        return <PublishingSettings />;
      case 'domains':
        return <DomainsSettings />;
      case 'analytics':
        return <AnalyticsSettings />;
      default:
        return <OrganizationSettings />;
    }
  };

  return (
    <div className="ink-page ink-page-flow">
      <div className="ink-account" data-enter-blocks="">
        <div>
          <div className="ink-account-title">Account</div>
          <nav>
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                data-active={activeSection === item.id ? 'true' : 'false'}
                onClick={() => openSection(item.id)}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>
        <div className="ink-account-main">{renderContent()}</div>
      </div>
    </div>
  );
}
